import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getIconClass, sortServices } from '../lib/utils';

const FALLBACK_SERVICES = [
  { title: { da: "Portservice & Eftersyn" }, service_slug: "portservice-og-lovpligtigt-eftersyn", icon: "fa-regular fa-clipboard-check" },
  { title: { da: "Garageporte" }, service_slug: "garageporte", icon: "fa-regular fa-garage" },
  { title: { da: "Friporte" }, service_slug: "friporte", icon: "fa-regular fa-door-open" },
  { title: { da: "Industriporte" }, service_slug: "industriporte", icon: "fa-regular fa-warehouse" },
  { title: { da: "Hurtigporte" }, service_slug: "hurtigporte", icon: "fa-regular fa-bolt" },
  { title: { da: "Alarmsystemer" }, service_slug: "alarmsystem", icon: "fa-regular fa-shield-security" },
  { title: { da: "Videoovervågning" }, service_slug: "alarmsystem-video-overvaagning", icon: "fa-regular fa-video" }
];

export default function Header() {
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setServices(data);
        }
      })
      .catch(err => console.error('Error fetching services for header:', err));
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesDropdownOpen(false);
    setIsMobileServicesOpen(false);
  }, [location]);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200/80 bg-white/95 backdrop-blur-md">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://assets.auxodev.dk/assets/a2859b51-1b34-4d15-a97b-9fa5c0c0144f/imgi-1-logo-1.png"
            alt="Pabst Teknik ApS – Portservice, Porte & Sikring"
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation (Visible above 1200px) */}
        <nav className="hidden items-center gap-8 min-[1200px]:flex">
          <Link
            to="/"
            className={`relative py-2 text-sm font-semibold transition-colors duration-200 ${
              isActive('/') ? 'text-brand-accent' : 'text-brand-dark hover:text-brand-accent'
            }`}
          >
            Forside
            {isActive('/') && (
              <span className="absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-brand-accent" />
            )}
          </Link>
          <Link
            to="/om-os"
            className={`relative py-2 text-sm font-semibold transition-colors duration-200 ${
              isActive('/om-os') ? 'text-brand-accent' : 'text-brand-dark hover:text-brand-accent'
            }`}
          >
            Om Pabst Teknik
            {isActive('/om-os') && (
              <span className="absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-brand-accent" />
            )}
          </Link>

          {/* Ydelser Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setIsServicesDropdownOpen(true)}
            onMouseLeave={() => setIsServicesDropdownOpen(false)}
          >
            <Link
              to="/ydelser"
              className={`relative py-2 flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200 ${
                isActive('/ydelser') ? 'text-brand-accent' : 'text-brand-dark hover:text-brand-accent'
              }`}
            >
              Vi tilbyder
              <i className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${isServicesDropdownOpen ? 'rotate-180' : ''}`} />
              {isActive('/ydelser') && (
                <span className="absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-brand-accent" />
              )}
            </Link>

            {/* Dropdown Menu */}
            {isServicesDropdownOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-[600px] rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl transition-all">
                <div className="grid grid-cols-2 gap-6">
                  {/* Column 1: Porte & Portservice */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-accent/10 text-brand-accent text-xs">
                        <i className="fa-solid fa-garage" />
                      </span>
                      <span className="text-xs font-bold uppercase tracking-wider text-brand-dark">Portservice & Porte</span>
                    </div>
                    <div className="flex flex-col gap-1 mt-1">
                      {sortServices(services)
                        .filter(s => !s.service_slug?.startsWith('alarmsystem'))
                        .map((service) => {
                          const isServiceActive = location.pathname === `/ydelser/${service.service_slug}`;
                          return (
                            <Link
                              key={service.service_slug}
                              to={`/ydelser/${service.service_slug}`}
                              className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                                isServiceActive 
                                  ? 'bg-brand-accent-light text-brand-accent' 
                                  : 'text-slate-700 hover:bg-slate-50 hover:text-brand-accent'
                              }`}
                            >
                              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] transition-colors ${
                                isServiceActive ? 'bg-brand-accent text-white' : 'bg-slate-100 text-brand-accent'
                              }`}>
                                <i className={getIconClass(service.icon)} />
                              </span>
                              <span className="truncate">{service.title.da}</span>
                            </Link>
                          );
                        })}
                    </div>
                  </div>

                  {/* Column 2: Sikring & Alarmer (Relativt nyt) */}
                  <div className="flex flex-col gap-2 border-l border-slate-100 pl-6">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-600 text-xs">
                        <i className="fa-solid fa-shield-halved" />
                      </span>
                      <span className="text-xs font-bold uppercase tracking-wider text-brand-dark">Sikringsløsninger</span>
                      <span className="ml-auto rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emerald-600 border border-emerald-200 uppercase">Nyhed</span>
                    </div>
                    <div className="flex flex-col gap-1 mt-1">
                      {services
                        .filter(s => s.service_slug?.startsWith('alarmsystem'))
                        .map((service) => {
                          const isServiceActive = location.pathname === `/ydelser/${service.service_slug}`;
                          return (
                            <Link
                              key={service.service_slug}
                              to={`/ydelser/${service.service_slug}`}
                              className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                                isServiceActive 
                                  ? 'bg-brand-accent-light text-brand-accent' 
                                  : 'text-slate-700 hover:bg-slate-50 hover:text-brand-accent'
                              }`}
                            >
                              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] transition-colors ${
                                isServiceActive ? 'bg-brand-accent text-white' : 'bg-slate-100 text-brand-accent'
                              }`}>
                                <i className={getIconClass(service.icon)} />
                              </span>
                              <span className="truncate">{service.title.da}</span>
                            </Link>
                          );
                        })}
                    </div>
                  </div>
                </div>

                <div className="mt-4 border-t border-slate-100 pt-3 flex items-center justify-between">
                  <span className="text-[11px] text-slate-500 font-medium">Din lokale port- og sikringspartner i Vejen</span>
                  <Link
                    to="/ydelser"
                    className="flex items-center gap-1.5 text-xs font-bold text-brand-accent hover:underline"
                  >
                    Se alle ydelser
                    <i className="fa-regular fa-arrow-right text-[10px]" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          <Link
            to="/galleri"
            className={`relative py-2 text-sm font-semibold transition-colors duration-200 ${
              isActive('/galleri') ? 'text-brand-accent' : 'text-brand-dark hover:text-brand-accent'
            }`}
          >
            Galleri
            {isActive('/galleri') && (
              <span className="absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-brand-accent" />
            )}
          </Link>
          <Link
            to="/kontakt"
            className={`relative py-2 text-sm font-semibold transition-colors duration-200 ${
              isActive('/kontakt') ? 'text-brand-accent' : 'text-brand-dark hover:text-brand-accent'
            }`}
          >
            Kontakt
            {isActive('/kontakt') && (
              <span className="absolute bottom-0 left-0 h-0.5 w-full rounded-full bg-brand-accent" />
            )}
          </Link>
        </nav>

        {/* Desktop CTA (Visible above 1200px) */}
        <div className="hidden items-center gap-4 min-[1200px]:flex">
          <a
            href="tel:+4521684422"
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-brand-dark shadow-sm transition-all hover:border-brand-accent hover:text-brand-accent"
          >
            <i className="fa-regular fa-phone text-brand-accent" />
            <span className="whitespace-nowrap">21 68 44 22</span>
          </a>
          <Link
            to="/kontakt"
            className="rounded-full bg-brand-accent px-6 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-brand-accent-hover"
          >
            Få et tilbud
          </Link>
        </div>

        {/* Mobile Menu Button (Visible below 1200px) */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-brand-dark shadow-sm transition-all hover:border-brand-accent hover:text-brand-accent min-[1200px]:hidden"
          aria-label="Toggle menu"
        >
          <i className={`fa-regular ${isMobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} />
        </button>
      </div>

      {/* Mobile Menu Overlay (Visible below 1200px) */}
      {isMobileMenuOpen && (
        <div className="fixed inset-x-0 top-20 h-[199vh] z-40 flex flex-col border-t border-slate-100 bg-white px-6 py-8 shadow-2xl overflow-y-auto min-[1200px]:hidden">
          <nav className="flex flex-col gap-5">
            <Link
              to="/"
              className={`flex items-center gap-2.5 text-lg font-bold transition-colors ${
                isActive('/') ? 'text-brand-accent' : 'text-brand-dark'
              }`}
            >
              {isActive('/') && <span className="h-2 w-2 rounded-full bg-brand-accent" />}
              Forside
            </Link>
            <Link
              to="/om-os"
              className={`flex items-center gap-2.5 text-lg font-bold transition-colors ${
                isActive('/om-os') ? 'text-brand-accent' : 'text-brand-dark'
              }`}
            >
              {isActive('/om-os') && <span className="h-2 w-2 rounded-full bg-brand-accent" />}
              Om Pabst Teknik
            </Link>

            {/* Expandable Mobile Ydelser */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                className={`flex items-center justify-between text-lg font-bold transition-colors ${
                  isActive('/ydelser') ? 'text-brand-accent' : 'text-brand-dark'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  {isActive('/ydelser') && <span className="h-2 w-2 rounded-full bg-brand-accent" />}
                  Vi tilbyder
                </span>
                <i className={`fa-regular fa-chevron-down text-sm transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {isMobileServicesOpen && (
                <div className="ml-4 flex flex-col gap-4 border-l-2 border-slate-100 pl-4 pt-1">
                  {/* Porte & Portservice */}
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-dark flex items-center gap-1.5">
                      <i className="fa-solid fa-garage text-brand-accent text-[11px]" /> Portservice & Porte
                    </span>
                    {sortServices(services)
                      .filter(s => !s.service_slug?.startsWith('alarmsystem'))
                      .map((service) => {
                        const isServiceActive = location.pathname === `/ydelser/${service.service_slug}`;
                        return (
                          <Link
                            key={service.service_slug}
                            to={`/ydelser/${service.service_slug}`}
                            className={`flex items-center gap-2 text-sm font-semibold transition-colors ${
                              isServiceActive ? 'text-brand-accent' : 'text-slate-600 hover:text-brand-accent'
                            }`}
                          >
                            {isServiceActive && <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" />}
                            {service.title.da}
                          </Link>
                        );
                      })}
                  </div>

                  {/* Sikringsløsninger */}
                  <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-dark flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <i className="fa-solid fa-shield-halved text-emerald-600 text-[11px]" /> Sikringsløsninger
                      </span>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[8px] font-bold text-emerald-600 border border-emerald-200 uppercase">Nyhed</span>
                    </span>
                    {sortServices(services)
                      .filter(s => s.service_slug?.startsWith('alarmsystem'))
                      .map((service) => {
                        const isServiceActive = location.pathname === `/ydelser/${service.service_slug}`;
                        return (
                          <Link
                            key={service.service_slug}
                            to={`/ydelser/${service.service_slug}`}
                            className={`flex items-center gap-2 text-sm font-semibold transition-colors ${
                              isServiceActive ? 'text-brand-accent' : 'text-slate-600 hover:text-brand-accent'
                            }`}
                          >
                            {isServiceActive && <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" />}
                            {service.title.da}
                          </Link>
                        );
                      })}
                  </div>

                  <Link
                    to="/ydelser"
                    className="text-sm font-bold text-brand-accent pt-1"
                  >
                    Se alt vi tilbyder →
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/galleri"
              className={`flex items-center gap-2.5 text-lg font-bold transition-colors ${
                isActive('/galleri') ? 'text-brand-accent' : 'text-brand-dark'
              }`}
            >
              {isActive('/galleri') && <span className="h-2 w-2 rounded-full bg-brand-accent" />}
              Galleri
            </Link>
            <Link
              to="/kontakt"
              className={`flex items-center gap-2.5 text-lg font-bold transition-colors ${
                isActive('/kontakt') ? 'text-brand-accent' : 'text-brand-dark'
              }`}
            >
              {isActive('/kontakt') && <span className="h-2 w-2 rounded-full bg-brand-accent" />}
              Kontakt
            </Link>
          </nav>

          {/* Mobile CTAs */}
          <div className="mt-auto flex flex-col gap-4 pt-8">
            <a
              href="tel:+4521684422"
              className="flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-slate-50 py-3.5 text-base font-bold text-brand-dark shadow-sm"
            >
              <i className="fa-regular fa-phone text-brand-accent text-lg" />
              <span>Ring op: 21 68 44 22</span>
            </a>
            <Link
              to="/kontakt"
              className="flex items-center justify-center rounded-xl bg-brand-accent py-3.5 text-base font-bold text-white shadow-md hover:bg-brand-accent-hover"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
